import { useState } from "react";
import type { ApiClient } from "../../api/client";
import type { Channel, LineRequest, Menu, MenuRules, ModifierGroup, Product, Promotions } from "../../api/types";
import { priceOf } from "../../features/promotions/promotions";
import {
  chooseVariant,
  coveredByAllVegetables,
  isComplete,
  itemsFor,
  maximumOf,
  missingGroups,
  newSelection,
  offersAllVegetables,
  optionPrice,
  sectionsOf,
  tapWithout,
  toggleAllVegetables,
  toggleChoice,
  toggleOnly,
  toLine,
  type Selection,
} from "../../features/sale/product-options";
import { OnlyButton } from "../../features/sale/ProductDialog";
import { useQuote } from "../../features/sale/useQuote";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { eurosToCents, formatEuro } from "../../lib/money";

interface Props {
  api: ApiClient;
  menu: Menu;
  rules: MenuRules;
  product: Product;
  /** The size it starts with: the drink's row, else the product's default, its smallest size. */
  variant: string | undefined;
  channel: Channel;
  promotions: Promotions | null;
  /** At most this many at once (a guest at the table: 50). */
  maxQuantity?: number;
  onAdd: (line: LineRequest) => void;
  onBack: () => void;
}

/**
 * The options of one product in the app (prototype obOptHtml): Formaat, the groups in the till's order
 * ("· verplicht", "· 2 gratis", then the price), Zonder with "Groenten", the instructions, "Opmerking
 * voor keuken", the quantity and "Toevoegen · €" with the server's price. In a window over the products, as
 * on the till (Furkan, 19/10/2026): the × or a tap beside it goes back to the products.
 */
export function OptionsView({
  api,
  menu,
  rules,
  product,
  variant,
  channel,
  promotions,
  maxQuantity = 999,
  onAdd,
  onBack,
}: Props) {
  const text = t.tafoApp.waiter;
  const [selection, setSelection] = useState<Selection>(() => newSelection(product, variant));
  const complete = isComplete(menu, product, selection);
  const line = toLine(product, selection);
  const priced = useQuote(api, complete ? { channel, lines: [line] } : null, JSON.stringify(promotions));
  const missing = missingGroups(menu, product, selection);
  const ingredients = product.ingredients ?? [];

  const freeOf = (group: ModifierGroup) => product.freeQuantities?.[group.id] ?? group.freeQuantity;
  /** "EXTRA GARNERINGEN", "SAUS NAAR KEUZE · 2 GRATIS", "PASTA · VERPLICHT" (prototype). */
  const groupTitle = (group: ModifierGroup) => {
    const free = freeOf(group);
    // "verplicht" in red until something is chosen, so it is not forgotten (Furkan, 19/10/2026), as the red
    // label of the till
    if (group.minimum > 0) {
      return (
        <>
          {group.title} · <span className={missing.includes(group) ? "ob-need" : undefined}>{text.required}</span>
        </>
      );
    }
    return free > 0 ? `${group.title} · ${text.freePicks(free)}` : group.title;
  };

  const groupChips = (group: ModifierGroup) => {
    const picked = selection.choices[group.id] ?? [];
    const full = picked.length >= maximumOf(group);
    const free = freeOf(group);
    return itemsFor(group, channel).map((item) => {
      const price = optionPrice(group, item, selection.variant);
      const on = picked.includes(item.productId);
      const position = on ? picked.indexOf(item.productId) : picked.length;
      const label =
        price === null
          ? ""
          : free > 0
            ? position < free
              ? text.freeLabel
              : formatEuro(price)
            : price > 0
              ? formatEuro(price)
              : "";
      return (
        <button
          key={item.productId}
          type="button"
          className="chip"
          aria-pressed={on}
          disabled={price === null || (full && !on && group.selectionMode === "MULTIPLE")}
          onClick={() => {
            setSelection(toggleChoice(selection, group, item.productId));
          }}
        >
          {menu.products[item.productId]?.name ?? item.productId}
          {label !== "" && <small className="num">{label}</small>}
        </button>
      );
    });
  };

  const withoutChips = () =>
    selection.onlyMode ? (
      ingredients.map((ingredient) => (
        <button
          key={ingredient}
          type="button"
          className="chip only"
          aria-pressed={selection.only.includes(ingredient)}
          onClick={() => {
            setSelection(toggleOnly(selection, ingredient));
          }}
        >
          {ingredient}
        </button>
      ))
    ) : (
      <>
        {offersAllVegetables(product, rules) && (
          <button
            type="button"
            className="chip minus zall"
            aria-pressed={selection.without.includes(rules.allVegetables)}
            onClick={() => {
              setSelection(toggleAllVegetables(selection, rules));
            }}
          >
            {rules.allVegetables}
          </button>
        )}
        {ingredients.map((ingredient) => {
          const covered = coveredByAllVegetables(selection, product, rules, ingredient);
          return (
            <button
              key={ingredient}
              type="button"
              className={`chip minus${covered ? " zoff" : ""}`}
              aria-pressed={selection.without.includes(ingredient)}
              title={covered ? t.product.allBut(ingredient) : undefined}
              onClick={() => {
                setSelection(tapWithout(selection, product, rules, ingredient));
              }}
            >
              {ingredient}
              {covered && <small>{text.gone}</small>}
            </button>
          );
        })}
      </>
    );

  const addBar = (
    <>
      <div className="ob-row ob-addbar">
        <div className="qty">
          <button
            type="button"
            aria-label={text.less}
            disabled={selection.quantity <= 1}
            onClick={() => {
              setSelection({ ...selection, quantity: selection.quantity - 1 });
            }}
          >
            −
          </button>
          <output aria-label={text.quantity}>{selection.quantity}</output>
          <button
            type="button"
            aria-label={text.more}
            onClick={() => {
              setSelection({ ...selection, quantity: Math.min(maxQuantity, selection.quantity + 1) });
            }}
          >
            +
          </button>
        </div>
        <button
          type="button"
          className="btn primary"
          disabled={!complete}
          onClick={() => {
            onAdd(line);
          }}
        >
          {text.add(priced.fresh && priced.quote ? formatEuro(priced.quote.totalCents) : "…")}
        </button>
      </div>
      {missing[0] && <p className="proto">{text.chooseFirst(missing[0].title)}</p>}
      {priced.error && <p className="error-text">{priced.error.message}</p>}
    </>
  );

  return (
    <Dialog title={product.name} size="product" onClose={onBack} footer={addBar}>
      {product.variants.length > 1 && (
        <>
          <div className="ph-sec">{text.size}</div>
          {/* the sizes side by side in equal columns, the price under the name (Furkan, 19/10/2026) */}
          <div
            className="ob-chips ob-sizes"
            style={{ gridTemplateColumns: `repeat(${String(Math.min(product.variants.length, 4))}, minmax(0, 1fr))` }}
          >
            {product.variants.map((item) => (
              <button
                key={item.label}
                type="button"
                className="chip"
                aria-pressed={selection.variant === item.label}
                onClick={() => {
                  setSelection(chooseVariant(menu, product, selection, item.label));
                }}
              >
                {item.label}
                <small className="num">
                  {formatEuro(eurosToCents(priceOf(promotions, channel, product, item.label)))}
                </small>
              </button>
            ))}
          </div>
        </>
      )}
      {sectionsOf(menu, product).map((section) =>
        section.kind === "without" ? (
          <section key="without" aria-label={t.product.without}>
            <div className="ph-sec ph-sec-only">
              {t.product.without}
              <OnlyButton selection={selection} onToggle={setSelection} />
              {selection.onlyMode && selection.only.length === 0 && <em>{t.product.onlyHint}</em>}
            </div>
            <div className="ob-chips">{withoutChips()}</div>
          </section>
        ) : (
          <section key={section.group.id} aria-label={section.group.title}>
            <div className="ph-sec">{groupTitle(section.group)}</div>
            <div className="ob-chips">{groupChips(section.group)}</div>
          </section>
        ),
      )}
      <input
        value={selection.note}
        maxLength={300}
        placeholder={text.kitchenNote}
        aria-label={text.kitchenNote}
        onChange={(event) => {
          setSelection({ ...selection, note: event.target.value });
        }}
      />
    </Dialog>
  );
}
